<template>
  <div class="page-shell teacher-list-page">
    <MobileTopNav title="名师列表" />

    <main class="teacher-list-section">
      <div class="page-container">
        <div class="teacher-head">
          <h2>名师列表</h2>
          <div class="teacher-divider">
            <span></span>
          </div>
        </div>

        <div class="teacher-grid">
          <article
            v-for="(item,index) in teacherCards"
            :key="index"
            class="teacher-card"
            role="button"
            tabindex="0"
            @click="goTeacherDetail(item)"
            @keydown.enter.prevent="goTeacherDetail(item)"
            @keydown.space.prevent="goTeacherDetail(item)"
          >
            <div class="teacher-photo">
              <img
                v-if="item.image"
                :src="item.image"
                :alt="item.name"
              >
              <div
                v-else
                class="teacher-photo-placeholder"
              >
                {{ item.name ? item.name.slice(0, 1) : '师' }}
              </div>
            </div>
            <p class="teacher-name">{{ item.name }}</p>
            <p
              v-if="item.info"
              class="teacher-info"
            >
              {{ item.info }}
            </p>
          </article>
        </div>

        <div
          v-if="loading"
          class="teacher-list-state"
        >
          加载中...
        </div>

        <div
          v-else-if="!teacherCards.length"
          class="teacher-list-state"
        >
          暂无名师信息
        </div>

        <div
          v-if="isMobile && finished && teacherCards.length"
          class="teacher-list-state teacher-list-state--muted"
        >
          已加载全部
        </div>

        <div
          v-if="!isMobile && total > pageSize"
          class="teacher-pagination"
        >
          <el-pagination
            background
            layout="prev, pager, next"
            :current-page="currentPage"
            :page-size="pageSize"
            :total="total"
            @current-change="handlePageChange"
          ></el-pagination>
        </div>
      </div>
    </main>
  </div>
</template>

<script>
import MobileTopNav from '../components/MobileTopNav.vue'
import {
  extractTeacherRows,
  extractTeacherTotal,
  formatTeacherCard
} from '../utils/teacher'

export default {
  name: 'TeacherListView',
  components: {
    MobileTopNav
  },
  data() {
    return {
      teacherCards: [],
      currentPage: 1,
      pageSize: 10,
      total: 0,
      loading: false,
      finished: false,
      isMobile: false,
      hasLoaded: false
    }
  },
  mounted() {
    document.title = '名师列表'
    this.handleResize()
    window.addEventListener('resize', this.handleResize)
    window.addEventListener('scroll', this.handleScroll, { passive: true })
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
    window.removeEventListener('scroll', this.handleScroll)
  },
  methods: {
    goTeacherDetail(item) {
      if (!item || !item.id) {
        return
      }

      this.$router.push({
        name: 'teacher-detail',
        params: {
          id: item.id
        }
      })
    },
    handleResize() {
      const nextIsMobile = window.innerWidth <= 768

      if (this.hasLoaded && this.isMobile === nextIsMobile) {
        return
      }

      this.isMobile = nextIsMobile
      this.currentPage = 1
      this.teacherCards = []
      this.finished = false
      this.hasLoaded = true
      this.fetchTeacherList()
    },
    async fetchTeacherList() {
      if (this.loading || (this.isMobile && this.finished)) {
        return
      }

      this.loading = true
      const page = this.currentPage

      try {
        const response = await this.$get('/teacher/listPage', {
          current: page,
          size: this.pageSize
        })
        const rows = extractTeacherRows(response)
        const nextCards = rows.map((item, index) =>
          formatTeacherCard(item, `${page}-${index}`)
        )
        const total = extractTeacherTotal(response)
        const hasTotal = total > 0

        this.total = total

        if (this.isMobile) {
          this.teacherCards = this.teacherCards.concat(nextCards)
          this.currentPage = page + 1
          this.finished =
            nextCards.length < this.pageSize ||
            (hasTotal && this.teacherCards.length >= total)
          return
        }

        this.teacherCards = nextCards
        this.finished = true
      } catch (error) {
        if (!this.teacherCards.length) {
          this.teacherCards = []
        }
        this.finished = true
      } finally {
        this.loading = false
      }
    },
    handlePageChange(page) {
      this.currentPage = page
      this.finished = false
      this.fetchTeacherList()
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },
    handleScroll() {
      if (!this.isMobile || this.loading || this.finished) {
        return
      }

      const scrollTop = window.pageYOffset || document.documentElement.scrollTop
      const viewportHeight = window.innerHeight
      const pageHeight = document.documentElement.scrollHeight

      if (scrollTop + viewportHeight >= pageHeight - 80) {
        this.fetchTeacherList()
      }
    }
  }
}
</script>

<style scoped>
.teacher-list-page {
  min-height: 100vh;
  background: #fff;
}

.teacher-list-section {
  width: 100%;
  min-height: 100vh;
  padding: 44px 0 64px;
}

.teacher-head {
  text-align: center;
}

.teacher-head h2 {
  font-size: 2rem;
  font-weight: 500;
  color: #4f4f4f;
}

.teacher-divider {
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 18px auto 34px;
}

.teacher-divider::before,
.teacher-divider::after {
  content: '';
  width: 92px;
  height: 2px;
  background: #013F7D;
}

.teacher-divider span {
  position: relative;
  width: 12px;
  height: 12px;
  margin: 0 12px;
  border-radius: 50%;
  background: #013F7D;
}

.teacher-divider span::before,
.teacher-divider span::after {
  content: '';
  position: absolute;
  top: 50%;
  width: 4px;
  height: 4px;
  margin-top: -2px;
  border-radius: 50%;
  background: #94c5e8;
}

.teacher-divider span::before {
  left: -10px;
}

.teacher-divider span::after {
  right: -10px;
}

.teacher-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 24px 18px;
}

.teacher-card {
  flex: 1 1 calc(50% - 9px);
  min-width: calc(50% - 9px);
  max-width: calc(50% - 9px);
  text-align: center;
  cursor: pointer;
}

.teacher-photo {
  position: relative;
  /* aspect-ratio: 1 / 1; */
  overflow: hidden;
  background: #e5e7f0;
}

.teacher-photo img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.teacher-photo-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #fff4e8 0%, #ffe0c0 100%);
  color: #ff7d29;
  font-size: 3rem;
  font-weight: 700;
}

.teacher-name {
  margin-top: 12px;
  font-size: 1rem;
  line-height: 1.5;
  color: #737373;
}

.teacher-info {
  display: -webkit-box;
  margin-top: 6px;
  overflow: hidden;
  color: #9aa0a8;
  font-size: 0.9rem;
  line-height: 1.6;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.teacher-list-state {
  padding: 28px 0 0;
  color: #8c94a3;
  font-size: 0.95rem;
  line-height: 1.6;
  text-align: center;
}

.teacher-list-state--muted {
  color: #b0b7c3;
}

.teacher-pagination {
  display: flex;
  justify-content: center;
  margin-top: 34px;
}

@media (min-width: 960px) {
  .teacher-card {
    flex-basis: calc(33.3333% - 12px);
    min-width: calc(33.3333% - 12px);
    max-width: calc(33.3333% - 12px);
  }
}

@media (min-width: 1360px) {
  .teacher-card {
    flex-basis: calc(25% - 14px);
    min-width: calc(25% - 14px);
    max-width: calc(25% - 14px);
  }
}

@media (max-width: 768px) {
  .teacher-list-section {
    min-height: calc(100vh - 48px);
    padding: 26px 0 40px;
  }

  .teacher-head {
    display: none;
  }

  .teacher-grid {
    gap: 16px 12px;
  }

  .teacher-card {
    flex-basis: calc(50% - 6px);
    min-width: calc(50% - 6px);
    max-width: calc(50% - 6px);
  }

  .teacher-name {
    margin-top: 8px;
    font-size: 0.92rem;
  }
}
</style>
